import React, {
    CSSProperties,
    ReactNode,
    useLayoutEffect,
    useRef,
} from 'react';

import { RowStore } from './row-index-store';
import RowTabStop, { NoRowTabStop } from './RowTabStop';

interface Props {
    index: number;
    /** react-window's position for the row, in a react-window list. */
    style?: CSSProperties;
    isFocusable: boolean;
    tabStopStore: RowStore<string>;
    /** The row's key, from the keys that the list gives useListTabStop. */
    tabStopKey: string;
    /**
     * Called when the row unmounts while focus is inside it. Keep one function
     * for the life of the list. A new function also runs the check.
     */
    onUnmountWithFocus?: () => void;
    children: ReactNode;
}

// The outer element of a row in a list with one Tab stop. A row the arrow
// keys skip never holds the Tab stop, so its controls are out of the Tab
// order too.
const ListRowFrame = ({
    index,
    style,
    isFocusable,
    tabStopStore,
    tabStopKey,
    onUnmountWithFocus,
    children,
}: Props) => {
    const rowRef = useRef<HTMLDivElement>(null);

    // React runs this cleanup before it removes the row element. So focus is
    // still inside the row at that time.
    useLayoutEffect(() => {
        const row = rowRef.current;
        return () => {
            if (row?.contains(document.activeElement)) {
                onUnmountWithFocus?.();
            }
        };
    }, [onUnmountWithFocus]);

    return isFocusable ? (
        <div ref={rowRef} style={style} data-row-index={index}>
            <RowTabStop
                rowRef={rowRef}
                store={tabStopStore}
                tabStopKey={tabStopKey}
            >
                {children}
            </RowTabStop>
        </div>
    ) : (
        <div ref={rowRef} style={style}>
            <NoRowTabStop rowRef={rowRef}>{children}</NoRowTabStop>
        </div>
    );
};

export default ListRowFrame;
